// GSTIN validation per the Indian government checksum standard.
//
// Format (15 characters): SSPPPPPPPPPPCEZK
//   SS       — 2-digit state code
//   PPPPPPPPPP — embedded PAN (5 letters, 4 digits, 1 letter)
//   C        — entity code (1..9, A..Z; distinguishes registrations under the same PAN)
//   Z        — literal 'Z' (fixed)
//   K        — checksum character (base-36)
//
// Checksum algorithm (official): treat each of the first 14 characters as a
// base-36 digit (0-9, A-Z). Multiply by factor 1 or 2 alternating (positions
// 1..14 use factors 1,2,1,2,...). For each product, divmod by 36 and sum the
// quotient + remainder. Take the total mod 36. The check digit is (36 - total)
// mod 36 expressed in base-36.

import { RuleViolationError } from '../errors.js';

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export type GstinValidationOk = {
  ok: true;
  gstin: string;
  pan: string;
  stateCode: string;
  entityCode: string;
};

export type GstinValidationErr = {
  ok: false;
  reason:
    | 'empty'
    | 'wrong_length'
    | 'wrong_format'
    | 'bad_pan_embed'
    | 'wrong_z_position'
    | 'bad_checksum';
};

export type GstinValidation = GstinValidationOk | GstinValidationErr;

function base36Val(c: string): number {
  const i = ALPHABET.indexOf(c);
  if (i < 0) throw new Error(`invalid base36 char "${c}"`);
  return i;
}

function base36Char(n: number): string {
  return ALPHABET[n]!;
}

export function validateGstin(input: string): GstinValidation {
  const s = input.trim().toUpperCase();
  if (s.length === 0) return { ok: false, reason: 'empty' };
  if (s.length !== 15) return { ok: false, reason: 'wrong_length' };
  if (!GSTIN_RE.test(s)) return { ok: false, reason: 'wrong_format' };
  if (s[13] !== 'Z') return { ok: false, reason: 'wrong_z_position' };
  const pan = s.slice(2, 12);
  if (!PAN_RE.test(pan)) return { ok: false, reason: 'bad_pan_embed' };

  let total = 0;
  for (let i = 0; i < 14; i++) {
    const v = base36Val(s[i]!);
    const factor = i % 2 === 0 ? 1 : 2;
    const prod = v * factor;
    total += Math.floor(prod / 36) + (prod % 36);
  }
  const check = (36 - (total % 36)) % 36;
  const expected = base36Char(check);
  if (expected !== s[14]) return { ok: false, reason: 'bad_checksum' };

  return {
    ok: true,
    gstin: s,
    pan,
    stateCode: s.slice(0, 2),
    entityCode: s[12]!,
  };
}

/** Throws RuleViolationError if the GSTIN is invalid; returns the normalized (upper-cased) GSTIN otherwise. */
export function requireValidGstin(input: string): string {
  const r = validateGstin(input);
  if (!r.ok) {
    throw new RuleViolationError('gstin.invalid', `Invalid GSTIN: ${r.reason}`, { input, reason: r.reason });
  }
  return r.gstin;
}

// GST state codes -> state / UT names, per the GSTN master list. Place of
// supply is stored as the two-digit code; this is only for showing a name.
export const GST_STATE_NAMES: Record<string, string> = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur',
  '15': 'Mizoram', '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '26': 'Dadra and Nagar Haveli and Daman and Diu', '27': 'Maharashtra', '28': 'Andhra Pradesh (old)',
  '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh',
  '38': 'Ladakh', '97': 'Other Territory',
};

/** Name for a GST state code, or for the two-digit prefix of a place-of-supply value. */
export function stateNameFromCode(codeOrPlace: string | null | undefined): string | null {
  if (!codeOrPlace) return null;
  return GST_STATE_NAMES[codeOrPlace.trim().slice(0, 2)] ?? null;
}

const STATE_CODE_BY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(GST_STATE_NAMES).map(([code, name]) => [name.toLowerCase(), code]),
);

/**
 * GST state code for a place-of-supply value as people actually enter it:
 * "33", "33 - Tamil Nadu", "Tamil Nadu" or "Tamil Nadu (33)". Null when it
 * matches neither a code nor a state name.
 */
export function stateCodeFromPlace(place: string | null | undefined): string | null {
  if (!place) return null;
  const trimmed = place.trim();
  const leading = /^(\d{2})(?!\d)/.exec(trimmed);
  if (leading && GST_STATE_NAMES[leading[1]!]) return leading[1]!;
  const inParens = /\((\d{2})\)\s*$/.exec(trimmed);
  if (inParens && GST_STATE_NAMES[inParens[1]!]) return inParens[1]!;
  const name = trimmed.replace(/\s*\(\d{2}\)\s*$/, '').replace(/\s+/g, ' ').toLowerCase();
  return STATE_CODE_BY_NAME[name] ?? null;
}
